import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { AllowedUser } from "@/lib/types";

interface State {
  loading: boolean;
  error: string | null;
  users: AllowedUser[];
}

export function useAllowedUsers(opts: { enabled: boolean }): State & {
  refetch: () => Promise<void>;
  add: (email: string, bootstrap?: boolean) => Promise<AllowedUser>;
  remove: (email: string) => Promise<void>;
} {
  const [state, setState] = useState<State>({ loading: false, error: null, users: [] });

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { users } = await api.listUsers();
      setState({ loading: false, error: null, users });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: (e as Error).message }));
    }
  }, []);

  useEffect(() => {
    if (!opts.enabled) return;
    void refetch();
  }, [opts.enabled, refetch]);

  const add = useCallback(async (email: string, bootstrap = false) => {
    const { user } = await api.addUser(email, { bootstrap });
    setState((s) => ({ ...s, users: [...s.users, user] }));
    return user;
  }, []);

  const remove = useCallback(async (email: string) => {
    await api.removeUser(email);
    setState((s) => ({ ...s, users: s.users.filter((u) => u.email !== email) }));
  }, []);

  return { ...state, refetch, add, remove };
}
